import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface PageEvent {
  id: string;
  session_id: string;
  event_type: string;
  event_label: string | null;
  section: string | null;
  extra: Record<string, unknown> | null;
  device: string | null;
  referrer: string | null;
  created_at: string;
}

interface Props {
  sessionId: string;
  leadName: string;
  onClose: () => void;
}

const EVENT_CONFIG: Record<string, { icon: string; color: string; bgColor: string; borderColor: string; label: string }> = {
  page_enter:      { icon: 'ri-login-circle-line',        color: '#4ade80',  bgColor: 'rgba(74,222,128,0.12)',  borderColor: 'rgba(74,222,128,0.3)',  label: 'Siteye Girdi' },
  page_leave:      { icon: 'ri-logout-circle-line',       color: '#f87171',  bgColor: 'rgba(248,113,113,0.12)', borderColor: 'rgba(248,113,113,0.3)', label: 'Siteden Ayrıldı' },
  button_click:    { icon: 'ri-cursor-line',              color: '#f0b429',  bgColor: 'rgba(240,180,41,0.12)',  borderColor: 'rgba(240,180,41,0.3)',  label: 'Butona Tıkladı' },
  modal_open:      { icon: 'ri-window-2-line',            color: '#f0b429',  bgColor: 'rgba(240,180,41,0.12)',  borderColor: 'rgba(240,180,41,0.3)',  label: 'Form Açtı' },
  modal_step2:     { icon: 'ri-arrow-right-circle-line',  color: '#f0b429',  bgColor: 'rgba(240,180,41,0.12)',  borderColor: 'rgba(240,180,41,0.3)',  label: 'Form Adım 2' },
  whatsapp_submit: { icon: 'ri-whatsapp-line',            color: '#4ade80',  bgColor: 'rgba(74,222,128,0.15)',  borderColor: 'rgba(74,222,128,0.4)',  label: 'WhatsApp Gönderdi' },
  scroll_depth:    { icon: 'ri-arrow-down-double-line',   color: '#00d4ff',  bgColor: 'rgba(0,212,255,0.10)',   borderColor: 'rgba(0,212,255,0.25)',  label: 'Scroll Etti' },
  section_view:    { icon: 'ri-eye-2-line',               color: '#a78bfa',  bgColor: 'rgba(167,139,250,0.10)', borderColor: 'rgba(167,139,250,0.25)', label: 'Bölüm Gördü' },
};

function formatClock(iso: string) {
  return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function formatFullDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function getEventDescription(ev: PageEvent): { title: string; detail: string | null } {
  switch (ev.event_type) {
    case 'page_enter': {
      const ref = ev.extra?.referrer as string | undefined;
      const screen = ev.extra?.screen as string | undefined;
      return {
        title: 'Siteye girdi',
        detail: ref && ref !== 'direct' ? `Kaynak: ${ref}${screen ? ` · Ekran: ${screen}` : ''}` : `Direkt ziyaret${screen ? ` · Ekran: ${screen}` : ''}`,
      };
    }
    case 'page_leave': {
      const secs = ev.extra?.seconds_on_page as number | undefined;
      const mins = secs ? Math.floor(secs / 60) : null;
      const remaining = secs ? secs % 60 : null;
      return {
        title: 'Siteden ayrıldı',
        detail: secs !== undefined ? `Toplam ${mins && mins > 0 ? `${mins} dk ` : ''}${remaining ?? secs} sn sitede kaldı` : null,
      };
    }
    case 'button_click':
      return { title: `"${ev.event_label}" butonuna bastı`, detail: ev.section ? `Bölüm: ${ev.section}` : null };
    case 'modal_open':
      return { title: 'WhatsApp iletişim formunu açtı', detail: ev.extra?.lead_context ? `İlgi: ${ev.extra.lead_context}` : null };
    case 'modal_step2':
      return { title: 'Formu 2. adıma taşıdı', detail: ev.extra?.sector ? `Sektör: ${ev.extra.sector}` : null };
    case 'whatsapp_submit':
      return { title: '✅ WhatsApp formunu gönderdi — LEAD OLDU!', detail: ev.extra?.action_type ? `Hedef: ${ev.extra.action_type}` : null };
    case 'scroll_depth':
      return { title: `Sayfanın %${ev.extra?.depth_percent ?? '?'}'ine kadar scroll etti`, detail: null };
    case 'section_view':
      return { title: `"${ev.section ?? ev.event_label}" bölümünü inceledi`, detail: null };
    default:
      return { title: ev.event_label ?? ev.event_type, detail: null };
  }
}

export default function BehaviorTimeline({ sessionId, leadName, onClose }: Props) {
  const [events, setEvents] = useState<PageEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('page_events')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data) setEvents(data as PageEvent[]);
        setLoading(false);
      });
  }, [sessionId]);

  const device = events[0]?.device ?? 'desktop';
  const referrer = events[0]?.referrer || 'Direkt';
  const leaveEvent = events.find((e) => e.event_type === 'page_leave');
  const totalSeconds = leaveEvent?.extra?.seconds_on_page as number | undefined;
  const isLead = events.some((e) => e.event_type === 'whatsapp_submit');
  const clickCount = events.filter((e) => e.event_type === 'button_click').length;
  const maxScroll = events
    .filter((e) => e.event_type === 'scroll_depth')
    .reduce((max, e) => Math.max(max, (e.extra?.depth_percent as number) ?? 0), 0);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-xl rounded-2xl flex flex-col"
        style={{
          background: 'linear-gradient(160deg, #0f0f1a 0%, #141428 100%)',
          border: '1px solid rgba(240,180,41,0.25)',
          boxShadow: '0 0 60px rgba(0,0,0,0.8)',
          maxHeight: '88vh',
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b border-white/8 flex-shrink-0 rounded-t-2xl"
          style={{ background: 'rgba(15,15,26,0.98)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.3)' }}>
              <i className="ri-route-line text-lg" style={{ color: '#f0b429' }} />
            </div>
            <div>
              <p className="text-white font-bold text-sm">{leadName} — Davranış Geçmişi</p>
              <p className="text-gray-500 text-xs">Ziyaret zaman çizelgesi</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-4 gap-0 border-b border-white/5 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.02)' }}>
          {[
            { icon: device === 'mobile' ? 'ri-smartphone-line' : 'ri-computer-line', label: 'Cihaz', value: device === 'mobile' ? 'Mobil' : 'Masaüstü', color: '#a78bfa' },
            { icon: 'ri-time-line', label: 'Süre', value: totalSeconds ? `${Math.floor(totalSeconds / 60)}dk ${totalSeconds % 60}sn` : '—', color: '#f0b429' },
            { icon: 'ri-cursor-line', label: 'Tıklama', value: `${clickCount} kez`, color: '#00d4ff' },
            { icon: 'ri-arrow-down-double-line', label: 'Max Scroll', value: maxScroll ? `%${maxScroll}` : '—', color: '#4ade80' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center justify-center py-3 px-2 border-r border-white/5 last:border-r-0">
              <i className={`${stat.icon} text-base mb-1`} style={{ color: stat.color }} />
              <p className="text-white text-xs font-bold">{stat.value}</p>
              <p className="text-gray-600 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* ── Lead badge ── */}
        {isLead && (
          <div className="flex items-center justify-center gap-2 py-2.5 border-b border-white/5 flex-shrink-0" style={{ background: 'rgba(74,222,128,0.06)' }}>
            <i className="ri-whatsapp-line text-green-400 text-sm" />
            <span className="text-green-400 text-xs font-bold">Bu ziyaretçi WhatsApp formunu doldurup gönderdi — Lead oldu!</span>
          </div>
        )}

        {/* ── Timeline ── */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: '#f0b429' }} />
              <p className="text-gray-500 text-sm">Veriler yükleniyor...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <i className="ri-inbox-line text-3xl text-gray-600" />
              <p className="text-gray-500 text-sm">Bu ziyaretçi için henüz kayıt yok</p>
              <p className="text-gray-600 text-xs">Tracking sistemi yeni lead'lerde otomatik çalışır</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px" style={{ background: 'linear-gradient(to bottom, rgba(240,180,41,0.3), rgba(240,180,41,0.05))' }} />

              <div className="space-y-1">
                {events.map((ev, idx) => {
                  const cfg = EVENT_CONFIG[ev.event_type] ?? { icon: 'ri-circle-line', color: '#6b7280', bgColor: 'rgba(107,114,128,0.1)', borderColor: 'rgba(107,114,128,0.2)', label: 'Olay' };
                  const { title, detail } = getEventDescription(ev);
                  const isLeadEvent = ev.event_type === 'whatsapp_submit';

                  // Date separator
                  const showDate = idx === 0 || formatFullDate(ev.created_at) !== formatFullDate(events[idx - 1].created_at);

                  return (
                    <div key={ev.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 mb-3 mt-2 ml-10">
                          <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
                          <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest whitespace-nowrap">{formatFullDate(ev.created_at)}</span>
                          <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
                        </div>
                      )}

                      <div className={`flex items-start gap-3 py-2 px-3 rounded-xl transition-colors ${isLeadEvent ? 'bg-green-400/5 border border-green-400/15' : 'hover:bg-white/[0.02]'}`}>
                        {/* Icon */}
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 z-10"
                          style={{ background: cfg.bgColor, border: `1px solid ${cfg.borderColor}` }}
                        >
                          <i className={`${cfg.icon} text-sm`} style={{ color: cfg.color }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <p className={`text-sm font-semibold leading-snug ${isLeadEvent ? 'text-green-400' : 'text-gray-100'}`}>
                            {title}
                          </p>
                          {detail && (
                            <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                          )}
                        </div>

                        {/* Time */}
                        <div className="flex-shrink-0 pt-0.5">
                          <span className="text-[11px] font-mono font-semibold" style={{ color: cfg.color, opacity: 0.8 }}>
                            {formatClock(ev.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 flex-shrink-0 rounded-b-2xl" style={{ background: 'rgba(15,15,26,0.8)' }}>
          <p className="text-gray-600 text-xs">{events.length} olay kaydedildi</p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
